import { create } from 'zustand';
import { getUnreadCount } from '../api/emailApi';

interface EmailStore {
  unreadCount: number;
  isLoading: boolean;
  lastUpdated: Date | null;
  refreshUnreadCount: (clientEndDate?: string | null, userRole?: string) => Promise<void>;
  setUnreadCount: (count: number) => void;
}

type SetState = (partial: EmailStore | Partial<EmailStore> | ((state: EmailStore) => EmailStore | Partial<EmailStore>)) => void;

export const useEmailStore = create<EmailStore>((set: SetState) => ({
  unreadCount: 0,
  isLoading: false,
  lastUpdated: null,
  
  refreshUnreadCount: async (clientEndDate?: string | null, userRole?: string) => {
    // Don't load email count for clients - email is only for staff (admin, manager, agent)
    const allowedRoles = ['admin', 'manager', 'agent', 'superadmin'];
    if (userRole && !allowedRoles.includes(userRole)) {
      set({ unreadCount: 0, isLoading: false });
      return;
    }
    
    // Don't load email count if subscription is ending soon (7 days or less)
    if (clientEndDate) {
      const end = new Date(clientEndDate);
      const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (diff <= 7) {
        // Skip loading to avoid 403 errors
        return;
      }
    }
    
    set({ isLoading: true });
    try {
      const count = await getUnreadCount();
      set({ 
        unreadCount: count, 
        isLoading: false,
        lastUpdated: new Date(),
      });
    } catch (err: any) {
      // Silently handle 403 errors for clients - expected behavior
      const isForbidden = err?.response?.status === 403;
      if (isForbidden) {
        set({ unreadCount: 0, isLoading: false });
        return;
      }
      
      // Don't log errors if subscription is ending soon (might be 403)
      if (clientEndDate) {
        const end = new Date(clientEndDate);
        const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        if (diff <= 7) {
          // Subscription ending soon, suppress error
          set({ isLoading: false });
          return;
        }
      }
      console.error('[emailStore] Failed to refresh unread count:', err);
      set({ isLoading: false });
    }
  },
  
  setUnreadCount: (count: number) => {
    set({ unreadCount: count, lastUpdated: new Date() });
  },
}));

